// Comprobante con forma de ticket (`.cv-modal` del mock del portal): cabecera verde, ticket punteado
// con la marca, las líneas, el total destacado y quién vendió; «Cerrar» al pie.
import type { ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import type { SaleReceipt } from '@/shared/lib/format/saleReceipt';
import type { SaleReceiptSale } from './SaleReceiptButton';
import { toSlotSale, type SaleSlots } from './saleSlots';
import { SaleBenefitsOutlet } from './SaleSlotOutlets';
import { SaleLineCard } from './SaleLineCard';

const BLOCK = 'border-t border-dashed border-border py-2 text-[11px]';

function money(cents: number): string {
    return formatMXN(cents / 100);
}

export function SaleReceiptTicket({
    open,
    onClose,
    sale,
    receipt,
    slots,
    brand,
}: {
    open: boolean;
    onClose: () => void;
    sale: SaleReceiptSale;
    receipt: SaleReceipt;
    slots: SaleSlots;
    brand: string;
}) {
    const slotSale = toSlotSale(
        sale,
        receipt.rows.map((r) => r.line),
    );
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose();
            }}
        >
            <DialogContent className="flex w-[460px] max-w-[92vw] flex-col gap-0 overflow-hidden rounded-xl p-0 sm:w-[460px] sm:p-0 [&>button:last-child]:right-4 [&>button:last-child]:top-3">
                <div className="flex-none border-b border-border bg-success-soft px-4 py-3">
                    <DialogTitle className="text-[14px] font-extrabold text-success">
                        Comprobante de venta
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Comprobante de la venta {sale.folio}
                    </DialogDescription>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
                    <div className="rounded-lg border border-dashed border-border px-4 py-3.5 font-mono">
                        <div className="mb-2.5 border-b border-dashed border-border pb-2.5 text-center font-sans">
                            <div className="inline-flex items-center gap-1.5 text-[14px] font-extrabold">
                                <span className="inline-flex size-5 items-center justify-center rounded bg-primary text-primary-foreground">
                                    <ShieldCheck className="size-3" aria-hidden />
                                </span>
                                {brand}
                            </div>
                            <div className="mt-[3px] text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Comprobante de venta
                            </div>
                        </div>

                        <div className="py-2 text-[11px]">
                            <TicketRow label="Cliente" value={sale.customerName} sans />
                            <ul
                                aria-label="Líneas del comprobante"
                                className="mt-1.5 flex flex-col gap-1.5 font-sans"
                            >
                                {receipt.rows.map((row) => (
                                    <SaleLineCard
                                        key={row.key}
                                        row={row}
                                        sale={slotSale}
                                        linePromotion={slots.linePromotion}
                                        giftName={slots.giftName}
                                        compact
                                        className="border-dashed"
                                    />
                                ))}
                            </ul>
                        </div>

                        <div className={BLOCK}>
                            <TicketRow label="Fecha" value={formatDateTimeMx(sale.soldAt)} sans />
                            <TicketRow
                                label="Folio venta"
                                value={<span className="break-all">{sale.folio}</span>}
                            />
                            {sale.paymentSummary && (
                                <TicketRow
                                    label="Forma de pago"
                                    value={
                                        <span data-testid="receipt-payment">
                                            {sale.paymentSummary}
                                        </span>
                                    }
                                    sans
                                />
                            )}
                            {receipt.totals && (
                                <>
                                    <TicketRow
                                        label="Financiado"
                                        value={money(receipt.totals.financedCents)}
                                    />
                                    <TicketRow
                                        label="De contado"
                                        value={money(receipt.totals.cashCents)}
                                    />
                                </>
                            )}
                            {sale.amountCents !== undefined && (
                                <TicketRow label="Total cobrado" value={money(sale.amountCents)} />
                            )}
                        </div>

                        <div className="my-1 border-y border-dashed border-border py-2.5 text-center">
                            <div className="font-sans text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                                Total de la venta
                            </div>
                            <div
                                data-testid="receipt-total"
                                className="mt-0.5 text-[28px] font-extrabold leading-tight tracking-tight text-success tabular-nums"
                            >
                                {money(receipt.totalCents)}
                            </div>
                        </div>

                        <SaleBenefitsOutlet
                            slot={slots.saleBenefits}
                            sale={slotSale}
                            className="mt-2 font-sans text-[11px]"
                        />

                        <div className="mt-2.5 border-t border-dashed border-border pt-2.5 text-center font-sans text-[10.5px] leading-normal text-muted-foreground">
                            Vendido en{' '}
                            <strong className="font-semibold text-foreground">
                                {sale.storeName ?? '—'}
                            </strong>{' '}
                            por{' '}
                            <strong className="font-semibold text-foreground">
                                {sale.sellerName ?? '—'}
                            </strong>
                        </div>
                    </div>
                </div>

                <div className="flex flex-none justify-end border-t border-border bg-muted/30 px-4 py-2.5">
                    <DialogClose className="inline-flex min-w-[100px] items-center justify-center rounded-md border border-border bg-card px-3 py-1.5 text-[11.5px] font-semibold transition-colors hover:border-primary/50 hover:bg-accent/40">
                        Cerrar
                    </DialogClose>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TicketRow({
    label,
    value,
    sans = false,
}: {
    label: string;
    value: ReactNode;
    /** El texto libre va en la letra normal; folios y cifras, en monoespaciada. */
    sans?: boolean;
}) {
    return (
        <div className="flex justify-between gap-2 py-0.5 tabular-nums">
            <span className="flex-none font-sans text-muted-foreground">{label}</span>
            <span className={cn('min-w-0 text-right font-bold', sans && 'font-sans')}>
                {value ?? '—'}
            </span>
        </div>
    );
}
